/**
 * Connection state of one subscription stream, or of every open stream
 * together.
 *
 * - `idle`: no stream is open, so the app holds no request.
 * - `connecting`: the client opened a request and waits for the server.
 * - `live`: the server accepted the stream, so events can arrive.
 * - `reconnecting`: the stream broke, and the client opens it again.
 * - `offline`: no stream is open, and the client retries nothing.
 */
export type RealTimeStatus =
  | 'idle'
  | 'connecting'
  | 'live'
  | 'reconnecting'
  | 'offline'

/**
 * The worst status first. {@link aggregateRealTimeStatus} reports the first
 * match, so a view never reads `live` while another stream is down.
 */
const STATUS_BY_SEVERITY: readonly RealTimeStatus[] = [
  'offline',
  'reconnecting',
  'connecting',
  'live',
]

/**
 * Reduces the status of every open subscription to the one status a view
 * renders. An empty list means nothing is subscribed, so the result is `idle`.
 */
export const aggregateRealTimeStatus = (
  statuses: readonly RealTimeStatus[],
): RealTimeStatus =>
  STATUS_BY_SEVERITY.find((status) => statuses.includes(status)) ?? 'idle'
